import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Exercise, ExerciseMetric, CircuitMetric, FireStoreMeasurement, Variants, Leaderboard } from "types";
import LeaderboardService from "services/LeaderboardService";
import * as Components from "./LeaderboardTableAbsolute.components";
import uniqBy from "lodash/uniqBy";
import Loader from "components/Loader/Loader";
import { useLoadingContext } from "components/LocalisedLoadingProvider";
import MessageWithLogo from "components/MessageWithLogo";
import useMeasurementsFilter from "features/Live/hooks/useMeasurementsFilter";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import useAllowedAthleteMeasurementFilter from "hooks/useAllowedAthleteMeasurementFilter";
import { firstBy } from "thenby";
import useTickerQueue from "components/Ticker/TickerProvider/useTickerQueue";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";
import LeaderboardTickerPortal from "../LeaderboardTickerPortal";
import useUpdateLeaderboardStore from "../hooks/useUpdateLeaderboardStore";
import { useTranslation } from "react-i18next";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";

interface LeaderboardTableAbsoluteProps {
  // Legacy props (for backward compatibility)
  exercise?: Exercise;
  metric?: ExerciseMetric | CircuitMetric;
  variant?: Variants | null;
  // New props
  leaderboard?: Leaderboard;
  startDate?: string;
  endDate?: string;
}

const LeaderboardTableAbsolute = memo(function LeaderboardTableAbsolute(props: LeaderboardTableAbsoluteProps) {
  const { t } = useTranslation();
  const organisation = useOrganisation();
  const hyroxMetadata = useHyroxMetadata();
  const {
    leaderboard,
    exercise: legacyExercise,
    metric: legacyMetric,
    startDate,
    endDate,
    variant: legacyVariant,
  } = props;
  const [loading, setLoading] = useLoadingContext();
  const [measurements, setMeasurements] = useState<{ [id: string]: FireStoreMeasurement | undefined }>({});
  const [queue, setQueue] = useTickerQueue<FireStoreMeasurement>();

  // Support both new leaderboard prop and legacy exercise/metric props
  const isScoreType = leaderboard?.measureType === "Score";
  const exercise = legacyExercise;
  const metric = legacyMetric;
  const variant = legacyVariant ?? null;

  // Get scoreMetadata for Score type leaderboards
  const scoreMetadata =
    isScoreType && leaderboard?.metricId
      ? hyroxMetadata.find((score) => score.field === leaderboard.metricId)
      : undefined;

  const metricField = isScoreType ? "score" : metric?.field;
  const sortOrder = isScoreType ? 1 : (metric?.sortType ?? 1); // Score: descending (1), default to descending
  const exerciseId = exercise?.id;

  const initialMeasurementsRef = useRef<boolean>(true);
  const tickerMeasurement: FireStoreMeasurement | undefined = queue[0];

  const updateMeasurementStore = useUpdateLeaderboardStore({
    initialMeasurementsRef,
    setMeasurements,
    setQueue,
  });

  useEffect(() => {
    if (leaderboard) {
      // Use new leaderboard object signature
      setLoading(true);
      const unsubscribe = LeaderboardService.subscribe(
        organisation.id,
        leaderboard,
        startDate!,
        endDate!,
        (snapshot) => {
          LeaderboardHelpers.onSnapshot(snapshot, updateMeasurementStore, { metricField: metricField! });

          if (!snapshot.metadata.fromCache) {
            initialMeasurementsRef.current = false;
          }

          setLoading(false);
        }
      );

      return () => {
        initialMeasurementsRef.current = true;
        setMeasurements({});
        setQueue([]);
        unsubscribe();
        setLoading(false);
      };
    } else if (exerciseId && metricField) {
      // Legacy support: create a temporary leaderboard object
      const tempLeaderboard: Leaderboard = {
        measureType: "Exercise",
        exerciseId,
        metricId: metricField,
        variant,
        id: "",
        name: "",
        groups: [],
        groupIds: [],
        type: "Day",
        comparePeriodType: null,
        rankType: undefined,
        startTime: null,
        endTime: null,
        startDateComparePeriod: null,
      };

      setLoading(true);
      const unsubscribe = LeaderboardService.subscribe(
        organisation.id,
        tempLeaderboard,
        startDate!,
        endDate!,
        (snapshot) => {
          LeaderboardHelpers.onSnapshot(snapshot, updateMeasurementStore, { metricField });

          if (!snapshot.metadata.fromCache) {
            initialMeasurementsRef.current = false;
          }

          setLoading(false);
        }
      );

      return () => {
        initialMeasurementsRef.current = true;
        setMeasurements({});
        setQueue([]);
        unsubscribe();
        setLoading(false);
      };
    }
  }, [
    endDate,
    exerciseId,
    leaderboard,
    metricField,
    organisation.id,
    setLoading,
    setQueue,
    sortOrder,
    startDate,
    updateMeasurementStore,
    variant,
  ]);

  const measurementsArray = useMemo(() => {
    if (!metricField) {
      return [];
    }

    const measurementsArray: FireStoreMeasurement[] = [];
    const allMeasurements = Object.values(measurements);

    allMeasurements
      .filter((measurement) => measurement && typeof measurement[metricField] === "number")
      .forEach((measurement) => measurement && measurementsArray.push(measurement));

    return measurementsArray.sort(firstBy(metricField, sortOrder === 0 ? "asc" : "desc"));
  }, [measurements, metricField, sortOrder]);

  const uniqueMeasurements = useMemo(
    () => uniqBy(!variant ? measurementsArray : measurementsArray.filter((m) => m.variant === variant), "athleteId"),
    [measurementsArray, variant]
  );
  const allowedAthleteMeasurements = useAllowedAthleteMeasurementFilter(uniqueMeasurements);
  const filteredMeasurements = useMeasurementsFilter(allowedAthleteMeasurements);

  const allowedTickerMeasurements = useAllowedAthleteMeasurementFilter(
    useMemo(() => [tickerMeasurement].filter(Boolean), [tickerMeasurement])
  );
  const filteredTickerMeasurements = useMeasurementsFilter(allowedTickerMeasurements);
  const filteredTickerMeasurement = useMemo(() => filteredTickerMeasurements.shift(), [filteredTickerMeasurements]);

  useEffect(() => {
    // if a ticker measurement exists but should not be shown bump the queue up 1
    if (tickerMeasurement && typeof filteredTickerMeasurement === "undefined") {
      setQueue((prevQueue) => prevQueue.slice(1));
    }
  }, [filteredTickerMeasurement, setQueue, tickerMeasurement]);

  if (loading && filteredMeasurements.length === 0) {
    return <Loader isSmall />;
  }

  if (filteredMeasurements.length === 0) {
    return <MessageWithLogo>{t("leaderboard.no_measurements_error")}</MessageWithLogo>;
  }

  return (
    <>
      {filteredMeasurements.map((measurement, index) => (
        <Components.LeaderboardTableAbsoluteRow
          key={measurement.id}
          position={index + 1}
          measurement={measurement}
          metric={metric}
          scoreMetadata={scoreMetadata}
        />
      ))}
      <LeaderboardTickerPortal measurementId={filteredTickerMeasurement?.id}>
        {filteredTickerMeasurement && (
          <Components.LeaderboardTableAbsoluteRow
            key={filteredTickerMeasurement.id}
            position={filteredMeasurements.indexOf(filteredTickerMeasurement) + 1 || null}
            measurement={filteredTickerMeasurement}
            metric={metric}
            scoreMetadata={scoreMetadata}
          />
        )}
      </LeaderboardTickerPortal>
    </>
  );
});

export default LeaderboardTableAbsolute;
